import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import {
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material';
import { format } from 'date-fns';

import { AgaveDataGrid } from '@/components/agave-data-grid';
import Breadcrumb from '@/components/breadcrumb';
import { canDownloadShippingReceipt, getOrderNumber } from '@/utils/shipping-receipt';
import { formatFulfillmentStatus } from '@/utils/fulfillment-status';

import type { IzziSaleOrder } from '@/domain/izzi';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import { useOrdersSection } from './use-orders-section';

const formatDate = (value: unknown) =>
  value ? format(new Date(value as string), 'dd/MM/yyyy HH:mm') : '—';

const getFulfillmentChipColor = (status?: string): 'success' | 'default' | 'warning' => {
  if (status?.toLowerCase() === 'fulfilled') {
    return 'success';
  }

  if (status?.toLowerCase() === 'processing') {
    return 'warning';
  }

  return 'default';
};

export default function OrdersSection() {
  const hooks = useOrdersSection();

  const columns: GridColDef<IzziSaleOrder>[] = [
    { field: 'customerSo', headerName: 'Customer SO', flex: 1, minWidth: 140 },
    { field: 'trackingId', headerName: 'Tracking', flex: 1, minWidth: 160 },
    {
      field: 'fulfillmentStatus',
      headerName: 'Estado Fulfillment',
      width: 170,
      renderCell: ({ value }: GridRenderCellParams<IzziSaleOrder, string>) => (
        <Chip
          label={formatFulfillmentStatus(value)}
          size="small"
          color={getFulfillmentChipColor(value)}
          variant="outlined"
        />
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Fecha de creación',
      width: 170,
      renderCell: ({ value }) => formatDate(value),
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 220,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: ({ row }: GridRenderCellParams<IzziSaleOrder>) => {
        const isDownloading = hooks.downloadingOrderId === row.id;
        const canDownload = canDownloadShippingReceipt(row);

        if (!canDownload) {
          return null;
        }

        return (
          <Button
            variant="outlined"
            size="small"
            startIcon={isDownloading ? <CircularProgress size={16} /> : <DownloadIcon />}
            disabled={isDownloading || hooks.isLoading}
            onClick={() => hooks.onDownloadShippingReceipt(row)}
          >
            Descargar acuse de envío
          </Button>
        );
      },
    },
  ];

  return (
    <div className="p-6 flex flex-col gap-4">
      <Breadcrumb
        sections={[{ label: 'Izzi' }, { label: 'Órdenes de venta' }]}
        description="Consulta órdenes de venta y descarga el acuse de envío para órdenes completadas"
        actions={
          <Tooltip title="Actualizar datos">
            <span>
              <IconButton onClick={hooks.fetchOrders} disabled={hooks.isLoading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        }
      />

      <Card variant="outlined">
        <CardContent>
          <div className="flex gap-3 mb-4 items-center flex-wrap">
            <TextField
              label="Customer SO"
              variant="filled"
              size="small"
              value={hooks.searchCustomerSo}
              onChange={(event) => hooks.onSearchCustomerSoChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  hooks.onSearch();
                }
              }}
              sx={{ minWidth: 260 }}
            />
            <Button variant="contained" onClick={hooks.onSearch} startIcon={<SearchIcon />}>
              Buscar
            </Button>
            {hooks.appliedCustomerSo && (
              <>
                <Button variant="outlined" color="inherit" onClick={hooks.onClearSearch}>
                  Limpiar filtro
                </Button>
                <Chip
                  label={`Filtro: ${hooks.appliedCustomerSo}`}
                  size="small"
                  onDelete={hooks.onClearSearch}
                />
              </>
            )}
          </div>

          <AgaveDataGrid
            rows={hooks.data.data.map((order) => ({
              ...order,
              orderNumber: getOrderNumber(order),
            }))}
            columns={columns}
            getRowId={(row) => row.id}
            rowHeight={56}
            paginationMode="server"
            rowCount={hooks.data.total}
            paginationModel={hooks.paginationModel}
            onPaginationModelChange={hooks.onPaginationModelChange}
            pageSizeOptions={[10, 20, 50]}
            loading={hooks.isLoading}
            sx={{ height: 520 }}
          />
        </CardContent>
      </Card>
    </div>
  );
}
